<template>
    <div v-if="hasVisibleTours" class="mt-9">
        <!-- Main tour package loop -->
        <h3 class="schedule-section-title text-lg lg:text-[22px] text-[var(--charcoal)] font-bold">Tour Overview</h3>
        <div v-for="tour in tourPackageData" :key="tour.package_id"
            class="permit-tax mt-3 bg-[var(--primary-light)] p-4 md:p-7 max-[767px]:mb-3 rounded-2xl shadow-sm border border-[var(--bone)]">
            <div class="flex flex-wrap -mx-3">
                <!-- Image Section -->
                <div class="w-full md:w-1/3 lg:w-1/4 max-w-full px-3">
                    <div class="images-sec max-[767px]:mb-3">
                        <img v-if="tour.HeaderImage" :src="tour.HeaderImage" :alt="tour.package_name"
                            :data-testid="`header-image-${tour.package_id}`"
                            class="rounded-2xl shadow-sm border border-[var(--bone)]" />
                        <div v-else
                            class="tour-image-placeholder bg-[var(--light-gray)] rounded-md w-full h-52 flex items-center justify-center flex-col p-3">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="1.5">
                                <path
                                    d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z">
                                </path>
                                <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                                <line x1="12" y1="22.08" x2="12" y2="12"></line>
                            </svg>
                            <p class="placeholder-text text-[15px] text-[#9CA3AF] uppercase mt-2">Image coming soon</p>
                        </div>
                    </div>
                </div>

                <!-- Content Section -->
                <div class="w-full md:w-2/3 lg:w-3/4 max-w-full px-3">
                    <div class="content-sec">
                        <h2 class="text-lg text-[var(--charcoal-dark)] font-semibold mb-2">{{ tour.package_name }}</h2>
                        <p class="text-base text-[var(--tone-gray)]">{{ tour.short_description }}</p>
                        <p class="text-base text-[var(--tone-gray)]" v-if="form?.tenant_id === 'kens'">
                            {{ tour.long_description }}
                        </p>
                    </div>

                    <!-- Duration & What to Bring Section -->
                    <div v-if="tour.duration || (thingsToBring && thingsToBring.some(item => item && item.trim() !== ''))" class="tour-start-row mt-3 pt-3 border-t border-[var(--bone)]">
                        <div v-if="tour.duration" class="duration">
                            <h2 class="text-lg text-[var(--charcoal-dark)] font-semibold mb-2">Duration</h2>
                            <button
                                class="btn-info bg-[var(--light-beige)] text-base text-[var(--dark-brownish-gray)] font-semibold px-4 py-1 rounded-full flex items-center">
                                {{ tour.duration }}
                            </button>
                        </div>
                        <div v-if="thingsToBring && thingsToBring.some(item => item && item.trim() !== '')" class="whattobring mt-3">
                            <h2 class="text-lg text-[var(--charcoal-dark)] font-semibold mb-2">What to Bring</h2>
                            <div class="bring-group flex flex-wrap">
                                <button v-for="(item, key) in thingsToBring" :key="key"
                                    class="btn-info mr-2 mb-2 bg-[var(--light-beige)] text-base text-[var(--dark-brownish-gray)] font-semibold px-4 py-1 rounded-full flex items-center">
                                    {{ item }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Hiking tour row (only when tenant is not 'kens') -->
        <div v-if="form?.tenant_id !== 'kens'" v-for="tour in tourPackageData" :key="`desc-${tour.package_id}`"
            class="hiking-tour-row mt-9 bg-[var(--primary-light)] p-4 md:p-7 max-[767px]:mb-3 rounded-2xl shadow-sm border border-[var(--bone)]">
            <div class="flex flex-wrap -mx-3">
                <div class="w-full md:w-1/3 lg:w-1/4 max-w-full px-3">
                    <div class="images-sec">
                        <img v-if="tour.DescriptionImage" :src="tour.DescriptionImage" :alt="tour.package_name"
                            :data-testid="`desc-image-${tour.package_id}`" />
                        <div v-else
                            class="tour-image-placeholder w-full h-full flex items-center justify-center flex-col p-3">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="1.5">
                                <path
                                    d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z">
                                </path>
                                <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                                <line x1="12" y1="22.08" x2="12" y2="12"></line>
                            </svg>
                            <p class="placeholder-text text-[15px] text-[#9CA3AF] uppercase mt-2">Image coming soon</p>
                        </div>
                    </div>
                </div>

                <div class="w-full md:w-2/3 lg:w-3/4 max-w-full px-3">
                    <div class="content-sec">
                        <h2 class="text-lg text-[var(--charcoal-dark)] font-semibold mb-2">About This Tour</h2>
                        <p class="text-base text-[var(--tone-gray)]">{{ tour.long_description }}</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'TourDetails',
    props: {
        tourPackageData: { type: Array, required: true, },
        form: { type: Object, default: () => ({}), },
        thingsToBring: { type: Array, default: () => [], },
    },
    computed: {
        hasVisibleTours() {
            // Show wrapper only if there are tours available
            // OR the form condition allows showing hiking tours
            return this.tourPackageData && this.tourPackageData.length > 0;
        },
    },
};
</script>
